// The browser's half of passkeys: turns the server's JSON options into what
// navigator.credentials wants (binary fields as ArrayBuffers) and the answer back
// into JSON (binary fields base64url). The server (pkg/passkey) always asks for a
// discoverable credential with user verification, so one passkey is a complete
// sign-in: no username, no password.
//
// Copied into apps/admin, apps/partners and apps/web (each is a standalone npm
// project); keep the copies identical.

export interface Options { publicKey: Record<string, any> }
export type CredentialJSON = Record<string, unknown>;

/** True where this browser can make and use passkeys. */
export function passkeysSupported(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;
}

export function b64uToBuffer(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(s.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufferToB64u(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const descriptors = (list: any[] | undefined) => (list ?? []).map((d) => ({ ...d, id: b64uToBuffer(d.id) }));

/** The server's creation options as PublicKeyCredentialCreationOptions. */
export function creationOptions(o: Options): PublicKeyCredentialCreationOptions {
  const p = o.publicKey;
  return { ...p, challenge: b64uToBuffer(p.challenge), user: { ...p.user, id: b64uToBuffer(p.user.id) }, excludeCredentials: descriptors(p.excludeCredentials) } as PublicKeyCredentialCreationOptions;
}

/** The server's request options as PublicKeyCredentialRequestOptions. */
export function requestOptions(o: Options): PublicKeyCredentialRequestOptions {
  const p = o.publicKey;
  return { ...p, challenge: b64uToBuffer(p.challenge), allowCredentials: descriptors(p.allowCredentials) } as PublicKeyCredentialRequestOptions;
}

/** A PublicKeyCredential as the JSON the server verifies. */
export function credentialJSON(c: PublicKeyCredential): CredentialJSON {
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const response: Record<string, unknown> = { clientDataJSON: bufferToB64u(r.clientDataJSON) };
  if (r.attestationObject) {
    response.attestationObject = bufferToB64u(r.attestationObject);
    response.transports = r.getTransports?.() ?? [];
  } else {
    response.authenticatorData = bufferToB64u(r.authenticatorData);
    response.signature = bufferToB64u(r.signature);
    if (r.userHandle) response.userHandle = bufferToB64u(r.userHandle);
  }
  return {
    id: c.id,
    rawId: bufferToB64u(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment ?? undefined,
    clientExtensionResults: c.getClientExtensionResults?.() ?? {},
    response,
  };
}

/** What went wrong, in words for the person at the screen. */
export class PasskeyError extends Error {
  cancelled: boolean;
  constructor(message: string, cancelled = false) {
    super(message);
    this.cancelled = cancelled;
  }
}

function explain(e: unknown, making: boolean): PasskeyError {
  const name = (e as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "AbortError")
    return new PasskeyError(making ? "Passkey setup was cancelled or timed out." : "Sign-in was cancelled or timed out.", true);
  if (name === "InvalidStateError") return new PasskeyError("This device already has a passkey for this account.");
  if (name === "SecurityError") return new PasskeyError("Passkeys only work on the real address of this site (https).");
  if (name === "NotSupportedError") return new PasskeyError("This device can't make a passkey that meets our requirements.");
  return new PasskeyError((e as Error)?.message || "Something went wrong with the passkey.");
}

/** Asks the browser to make a passkey from the server's options. */
export async function createPasskey(o: Options): Promise<CredentialJSON> {
  if (!passkeysSupported()) throw new PasskeyError("This browser doesn't support passkeys.");
  try {
    const c = (await navigator.credentials.create({ publicKey: creationOptions(o) })) as PublicKeyCredential | null;
    if (!c) throw new PasskeyError("No passkey was made.", true);
    return credentialJSON(c);
  } catch (e) {
    throw e instanceof PasskeyError ? e : explain(e, true);
  }
}

/** Asks the browser to sign in with a passkey it holds for this site. */
export async function getPasskey(o: Options, signal?: AbortSignal): Promise<CredentialJSON> {
  if (!passkeysSupported()) throw new PasskeyError("This browser doesn't support passkeys.");
  try {
    const c = (await navigator.credentials.get({ publicKey: requestOptions(o), signal })) as PublicKeyCredential | null;
    if (!c) throw new PasskeyError("No passkey was chosen.", true);
    return credentialJSON(c);
  } catch (e) {
    throw e instanceof PasskeyError ? e : explain(e, false);
  }
}
